import { GuestProfileFormPatch } from "@closerplatform/spinner-openapi";
import { config } from "@fidget/common/config/config";
import { getMessages, intl, L10nData } from "@fidget/common/i18n";
import { Language } from "@fidget/common/models/language";
import { PickerWidgetMessages } from "@fidget/common/models/pickerWidgetMessages";
import { DefaultArtichokeService } from "@fidget/common/services/artichokeService";
import { WsGlobalEventsListener } from "@fidget/common/services/eventsListener";
import { windowProxyFactory } from "@fidget/common/services/windowProxyFactory";
import { allowWindowClosing, disallowWindowClosing } from "@fidget/common/services/windowUtils";
import {
  injectCSS,
  logger,
  makeQueryString,
  onDOMContentLoaded,
  parseQueryString,
  spyOnActions
} from "@fidget/common/utils";
import { ServerLogger } from "@fidget/common/utils/logger/serverLogger";
import { autorun, runInAction } from "mobx";
import { observer, Provider } from "mobx-react";
import React from "react";
import ReactDOM from "react-dom";
import { RawIntlProvider } from "react-intl";
import { ApiActions } from "./actions/api";
import { envActions } from "./actions/env";
import { userActions } from "./actions/user";
import { MainContainer } from "./containers/main/mainContainer";
import {
  ForeContext,
  ForeContextClass,
  ForeContextInitialData,
  GuestProfileContext,
  GuestProfileContextClass
} from "./context";
import { ForeRootStore, ForeRootStoreEnv, IForeRootStore } from "./models";
import { ForeCallService } from "./services/ForeCallService";
import { HostWindowRequests } from "./services/hostWindowRequests";
import { ForeSpinnerService } from "./services/spinnerService";
import { getLocaleFromBrowser } from "@fidget/common/services/language";
import { ForeServices } from "./services/foreServices";
import { AuthErrors } from "./services/authService";
import { fileService } from "@fidget/common/services/files/fileService";
import { getCurrentMaintenanceState } from "@fidget/common/utils/maintenance";
const version = require("../../../version.json").version;

require("@formatjs/intl-pluralrules/polyfill");
require("@formatjs/intl-pluralrules/locale-data/en");
require("@formatjs/intl-pluralrules/locale-data/pl");
require("@formatjs/intl-pluralrules/locale-data/ka");
require("@formatjs/intl-pluralrules/locale-data/es");

require("@formatjs/intl-relativetimeformat/polyfill");
require("@formatjs/intl-relativetimeformat/locale-data/en");
require("@formatjs/intl-relativetimeformat/locale-data/pl");
require("@formatjs/intl-relativetimeformat/locale-data/ka");
require("@formatjs/intl-relativetimeformat/locale-data/es");

spyOnActions();

export const apiActions = new ApiActions();

interface Props {
  foreRootStore: IForeRootStore;
}

const Main = observer(({ foreRootStore }: Props) => (
  <Provider {...foreRootStore}>
    <RawIntlProvider value={intl.intl}>
      <MainContainer />
    </RawIntlProvider>
  </Provider>
));

onDOMContentLoaded(async () => {
  logger.debug("Closer widget initializing");

  const query = parseQueryString();
  const orgId = query["orgId"];
  if (!orgId) {
    logger.error("Closer widget cannot initialize - no orgId provided");
    throw new Error("orgId is not set");
  }
  const parentOrigin = query["origin"];
  if (!parentOrigin) {
    logger.error("Closer widget cannot initialize - no origin provided");
    throw new Error("origin is not set");
  }

  const language = query["lang"] as Language;
  if (!language) {
    logger.error("Closer widget cannot initialize - no lang provided");
    throw new Error("lang is not set");
  }

  const tokens = {
    continueConversationToken: query["cc"]!,
    joinMeetingToken: query["jm"]!,
    cancelMeetingToken: query["cm"]!
  };
  const hostWindowRequests = HostWindowRequests.getInstance(
    windowProxyFactory.fromWindow(window.parent, parentOrigin)
  );
  const autoClientCalling = query["acc"] === "true";
  const forceNewUser = query["fnu"] === "true";
  const alwaysAutoCall = query["aac"] === "true" && autoClientCalling;
  const callingOnAssigneeChanged = query["cac"] === "true";
  const botAgentName = query["ban"];
  const callToNewAssigneeMessage = query["cnam"];
  const traceId = query["tid"];

  logger.debug(
    `Closer widget: forceNewUser: ${forceNewUser}, acc: ${autoClientCalling}, aac: ${alwaysAutoCall}, cac: ${callingOnAssigneeChanged}, ban: ${botAgentName}, cnam: ${callToNewAssigneeMessage}`
  );

  const contextInitalData: ForeContextInitialData = {
    orgId,
    language,
    hostWindowRequests,
    tokens,
    autoClientCalling,
    alwaysAutoCall,
    callingOnAssigneeChanged,
    botAgentName,
    callToNewAssigneeMessage,
    forceNewUser
  };

  if (config.services.serverLoggerEnabled) {
    logger.addLogger(new ServerLogger({ traceId, app: "fore" }));
  }

  const artichokeService = new DefaultArtichokeService();
  const spinnerService = ForeSpinnerService.getInstance();
  const callService = new ForeCallService(spinnerService, artichokeService);
  const services: ForeServices = { artichokeService, spinnerService, callService };
  const foreContext: ForeContext = new ForeContextClass(contextInitalData, services);
  const guestProfileContext: GuestProfileContext = new GuestProfileContextClass();

  await envActions.setOrgConfig(foreContext);

  const env: ForeRootStoreEnv = {
    artichokeService,
    spinnerService,
    eventListener: new WsGlobalEventsListener(artichokeService),
    foreContext,
    modelsGetter: {
      sharedOrgConfigGetter: () => foreContext.state.shared.orgConfig
    },
    callService
  };

  const foreRootStore = ForeRootStore.create(
    {
      foreCallStore: {},
      uiStore: {
        isWidgetInitializing: true
      },
      profanityStore: {},
      contextStore: {},
      fileStore: {},
      fingerprintStore: {},
      featuresStore: {}
    },
    env
  );

  autorun(() => {
    if (foreRootStore.foreCallStore.isNoCall) {
      allowWindowClosing();
    } else {
      disallowWindowClosing();
    }
  });

  foreContext.hostWindowRequests.onUrlParams(async (params) =>
    foreRootStore.featuresStore.processParams(params)
  );
  foreContext.hostWindowRequests.onGetUrlTagsProactiveMessages(() => foreContext.fidgetService.getConfig());

  foreContext.hostWindowRequests.onSetSideBarMode((mode) => {
    runInAction("setSideBarMode", () => (foreContext.state.sideBarMode = mode));
    return Promise.resolve();
  });

  const loadMainNotification = async () => {
    try {
      const notification = await env.spinnerService.getMainNotification(foreContext.language);

      if (notification) {
        foreRootStore.uiStore.setMainNotification(notification.text);
      }
    } catch (e) {
      logger.error({ message: "Couldn't load main notification", error: e });
    }
  };

  foreContext.hostWindowRequests.onChatVisibilityChanged(({visible, initializeBeforeRender}) => {
    runInAction("chatVisibilityChanged", async () => {
      if (visible || initializeBeforeRender) {
        const maintenanceWindows = foreContext.state.shared.orgConfig?.maintenanceWindows || [];
        const { message: maintenanceMessage, enabled: maintenanceEnabled } = getCurrentMaintenanceState(maintenanceWindows);
        if (maintenanceEnabled) {
          foreContext.hostWindowRequests.maintenanceModeEnabled(maintenanceMessage || "");
        } else {
          logger.debug("widget is visible");
          // when user makes widget visible make sure that the user is signed-up
          if (!foreContext.state.profile) {
            try {
              await foreContext.fidgetService.checkLimit();
            } catch {
              logger.info("Closer fore: rate limit exceeded");
              envActions.handleFailedInitialization(foreContext, foreRootStore, AuthErrors.RateLimitExceeded);
              return;
            }
            logger.debug("Closer fore: acquiring session on open");
            envActions
              .signUp(foreContext, foreRootStore, guestProfileContext)
              .catch((e) => {
                logger.debug("Closer fore: signup user error: ", e);
                throw e;
              })
              .then(loadMainNotification)
              .then(() => {
                foreContext.state.chatIsVisible = visible;
                userActions.markAllMessages(foreContext);
              })
              .then(() => foreContext.spinnerService.identifyUser({ widgetIsOpen: visible }))
              .catch((e) => logger.error({ message: "Closer fore: failed to prepare", error: e }));

            return;
          } else {
            spinnerService
              .identifyUser({ widgetIsOpen: visible })
              .catch((err) =>
                logger.error({ message: "Couldn't patch user on visibility changed", error: err })
              );
          }
          userActions.markAllMessages(foreContext);
        }
      }
      foreContext.state.chatIsVisible = visible;
    });
    return Promise.resolve();
  });

  foreContext.hostWindowRequests.onWindowResized(async ({ breakingPoints, browserWindowSize }) => {
    runInAction("windowResized", () => {
      foreContext.state.breakingPoints = breakingPoints;
      if (foreContext.state.profile) {
        foreContext.spinnerService.patchBrowserSize(browserWindowSize);
      }
    });
  });

  foreContext.hostWindowRequests.onCurrentPageChanged((currentPage) => {
    runInAction("currentPageChanged", () => (foreContext.state.currentPage = currentPage));
    guestProfileContext
      .patchGuestProfile({
        backOfficeData: [
          { key: "currentPage", value: currentPage.url },
          { key: "currentPageTitle", value: currentPage.title }
        ]
      })
      .catch((err) => logger.error({ message: "Couldn't patch user on page changed", error: err }));
    return Promise.resolve();
  });

  foreContext.hostWindowRequests.onTabActiveChanged((isTabActive) => {
    runInAction(() => {
      foreContext.state.tabIsActive = isTabActive;
      if (isTabActive) {
        userActions.markAllMessages(foreContext);
      }
    });
    return Promise.resolve();
  });

  foreContext.hostWindowRequests.onForgetClientRequest((reinit: boolean) => {
    runInAction(() => {
      envActions.forgetMe(foreContext, reinit);
    });
    return Promise.resolve();
  });

  foreContext.hostWindowRequests.onIdentifyUser(async (user) => {
    const userData: GuestProfileFormPatch = {
      firstName: user.firstName || undefined,
      lastName: user.lastName || undefined,
      email: user.email || undefined,
      phone: user.phone || undefined,
      languageLocale: user.languageLocale || undefined,
      zoneId: user.zoneId || undefined,
      location: user.location || undefined,
      tags: user.tags as string[] // note: `as`: readonly string[] -> string[]
    };

    const backOfficeData = Object.keys(user.additionalData || {}).map((key) => {
      const data = user.additionalData![key];
      const value = data === undefined || data === "" ? undefined : data;
      return { key, value };
    });

    try {
      const { tagGroupId } = await guestProfileContext.patchGuestProfile({ ...userData, backOfficeData });
      if (tagGroupId) {
        foreContext.handleTagGroupChange(tagGroupId);
      }
    } catch (err) {
      logger.error({ message: "Couldn't patch user data", error: err });
    }
  });

  foreContext.hostWindowRequests.onPickerWidgetMessagesSet(
    (pickerWidgetMessages: PickerWidgetMessages) => {
      foreContext.state.pickerWidgetMessages = pickerWidgetMessages;
      return Promise.resolve();
    }
  );

  foreContext.hostWindowRequests.onClientEndedCobrowsing(() => {
    userActions.sendCobrowsingEnded(foreContext);
    return Promise.resolve();
  });

  foreContext.hostWindowRequests.sendWidgetReady(config);

  foreContext.authService
    .getStoredCredentials()
    .then(async (credentials) => {
      spinnerService.setApiKey(credentials?.apiKey);
      fileService.setApiKey(credentials?.apiKey);

      await foreRootStore.fingerprintStore.generate();
      const { fingerprint } = foreRootStore.fingerprintStore;
      if (fingerprint) {
        spinnerService.setFingerprint(fingerprint);
        fileService.setFingerprint(fingerprint);
      }

      return credentials?.id ? spinnerService.getAssignee(credentials?.id) : undefined;
    })
    .then((assignee) => {
      apiActions.assignedAgentLoaded(foreContext, assignee?.assignee);
    });

  autorun(() => {
    foreContext.hostWindowRequests.setIframeWidth(
      foreRootStore.foreCallStore.videoExpanded && foreRootStore.foreCallStore.isInProgress
    );
  });

  const messages: L10nData = await getMessages(language);
  const locale = getLocaleFromBrowser(language);
  intl.setMessages(locale, messages);
  try {
    await Promise.all([
      foreRootStore.fingerprintStore.generate(),
      injectCSS("/fore.css?" + makeQueryString({ ver: version }))
    ]);

    const { fingerprint } = foreRootStore.fingerprintStore;

    if (fingerprint) {
      env.spinnerService.setFingerprint(fingerprint);
      fileService.setFingerprint(fingerprint);
    }

    ReactDOM.render(<Main foreRootStore={foreRootStore} />, document.getElementById("app"));
  } catch (e) {
    logger.error({ message: "css not loaded, won't render", error: e });
  }
});
